import { useState, useEffect } from "react";

const STORAGE_KEY = "intentdrive_onboarding_complete";

interface TourStep {
  title: string;
  text: string;
  // Fixed position for the floating card (top, left in viewport)
  top: string;
  left: string;
}

const steps: TourStep[] = [
  {
    title: "Welcome to IntentDrive!",
    text: "This is your dashboard where you manage your revenue routers.",
    top: "30%",
    left: "50%",
  },
  {
    title: "Use a Template",
    text: "Click 'Use a Template' to start with a pre-built multi-offer flow.",
    top: "25%",
    left: "50%",
  },
  {
    title: "Analytics",
    text: "Check your Analytics to see real-time RPUS and conversion data.",
    top: "20%",
    left: "20rem",
  },
  {
    title: "Verticals",
    text: "Configure your Verticals to set payout scores and eligibility rules.",
    top: "28%",
    left: "20rem",
  },
  {
    title: "Embed Code",
    text: "Ready to go live? Generate an embed code for any website.",
    top: "50%",
    left: "20rem",
  },
];

const OnboardingTour = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const done = localStorage.getItem(STORAGE_KEY);
    if (!done) {
      setVisible(true);
    }
  }, []);

  const finish = () => {
    localStorage.setItem(STORAGE_KEY, "true");
    setVisible(false);
  };

  const next = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      finish();
    }
  };

  if (!visible) return null;

  const step = steps[currentStep];
  const isLast = currentStep === steps.length - 1;

  return (
    <div className="fixed inset-0 z-[9999]" style={{ pointerEvents: "auto" }}>
      {/* Semi-transparent overlay */}
      <div className="absolute inset-0 bg-black/50" />

      {/* Floating card */}
      <div
        className="absolute w-[340px] max-w-[90vw] rounded-xl bg-white text-slate-900 shadow-2xl p-6"
        style={{
          top: step.top,
          left: step.left,
          transform: "translate(-50%, -50%)",
        }}
      >
        {/* Step counter */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-emerald-600 uppercase tracking-wide">
            Step {currentStep + 1} of {steps.length}
          </span>
          <button
            onClick={finish}
            className="text-xs text-slate-400 hover:text-slate-600 transition-colors"
          >
            Skip Tour
          </button>
        </div>

        {/* Title */}
        <h3 className="text-lg font-bold mb-2">{step.title}</h3>

        {/* Body */}
        <p className="text-sm text-slate-600 mb-5 leading-relaxed">
          {step.text}
        </p>

        {/* Action button */}
        <button
          onClick={next}
          className="w-full py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-semibold transition-colors"
        >
          {isLast ? "Get Started!" : "Next"}
        </button>

        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mt-4">
          {steps.map((_, i) => (
            <div
              key={i}
              className={`h-1.5 rounded-full transition-all ${
                i === currentStep
                  ? "w-4 bg-emerald-500"
                  : i < currentStep
                    ? "w-1.5 bg-emerald-300"
                    : "w-1.5 bg-slate-300"
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
